import React, { useState, useEffect } from 'react';
import CodeEditor from './CodeEditor';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ReactPlayground = () => {
  const [selectedExample, setSelectedExample] = useState('counter');
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);

  const examples = [
    {
      id: 'counter',
      name: 'Counter Component',
      icon: 'Plus',
      code: `import React, { useState } from 'react';

const Counter = () => {
  const [count, setCount] = useState(0);

  return (
    <div className="p-6 bg-white rounded-lg shadow-soft">
      <h2 className="text-2xl font-bold mb-4">Counter: {count}</h2>
      <div className="space-x-2">
        <button 
          onClick={() => setCount(count - 1)}
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Decrease
        </button>
        <button 
          onClick={() => setCount(count + 1)}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Increase
        </button>
        <button 
          onClick={() => setCount(0)}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default Counter;`
    },
    {
      id: 'todo',
      name: 'Todo List',
      icon: 'CheckSquare',
      code: `import React, { useState } from 'react';

const TodoList = () => {
  const [todos, setTodos] = useState([
    { id: 1, text: 'Learn React', completed: false },
    { id: 2, text: 'Build awesome apps', completed: false }
  ]);
  const [inputValue, setInputValue] = useState('');

  const addTodo = () => {
    if (inputValue.trim()) {
      setTodos([...todos, {
        id: Date.now(),
        text: inputValue,
        completed: false
      }]);
      setInputValue('');
    }
  };

  const toggleTodo = (id) => {
    setTodos(todos.map(todo =>
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-soft max-w-md">
      <h2 className="text-xl font-bold mb-4">Todo List</h2>
      <div className="flex mb-4">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addTodo()}
          className="flex-1 px-3 py-2 border rounded-l"
          placeholder="Add new todo..."
        />
        <button
          onClick={addTodo}
          className="px-4 py-2 bg-blue-500 text-white rounded-r hover:bg-blue-600"
        >
          Add
        </button>
      </div>
      <ul className="space-y-2">
        {todos.map(todo => (
          <li key={todo.id} className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={todo.completed}
              onChange={() => toggleTodo(todo.id)}
              className="rounded"
            />
            <span className={todo.completed ? 'line-through text-gray-500' : ''}>
              {todo.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TodoList;`
    },
    {
      id: 'form',
      name: 'Contact Form',
      icon: 'Mail',
      code: `import React, { useState } from 'react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-soft max-w-md">
      <h2 className="text-xl font-bold mb-4">Contact Form</h2>
      {submitted ? (
        <div className="text-green-600 text-center py-8">
          <div className="text-4xl mb-2">✓</div>
          <p>Message sent successfully!</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Send Message
          </button>
        </form>
      )}
    </div>
  );
};

export default ContactForm;`
    }
  ];

  useEffect(() => {
    const example = examples?.find(ex => ex?.id === selectedExample);
    if (example) {
      setCode(example?.code);
    }
  }, [selectedExample]);

  const handleRun = async (codeToRun) => {
    setIsRunning(true);
    setError(null);
    
    try {
      // Simulate code execution
      await new Promise(resolve => setTimeout(resolve, 1000));
      setOutput(`Component "${examples?.find(ex => ex?.id === selectedExample)?.name}" rendered successfully!\n\nThis is a preview of how your React component would appear in a real application. The component includes:\n- Interactive state management\n- Event handlers\n- Responsive styling\n- Accessibility features`);
    } catch (err) {
      setError(err?.message);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Example Selector */}
      <div className="flex flex-wrap gap-2">
        {examples?.map((example) => (
          <Button
            key={example?.id}
            variant={selectedExample === example?.id ? 'default' : 'outline'}
            size="sm"
            iconName={example?.icon}
            iconPosition="left"
            onClick={() => setSelectedExample(example?.id)}
            className="transition-all duration-300"
          >
            {example?.name}
          </Button>
        ))}
      </div>
      {/* Code Editor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
            <Icon name="Code2" size={20} className="text-accent" />
            <span>React Component</span>
          </h3>
          <CodeEditor
            title={`${examples?.find(ex => ex?.id === selectedExample)?.name}.jsx`}
            language="jsx"
            initialCode={code}
            onCodeChange={setCode}
            onRun={handleRun}
            isRunning={isRunning}
            showOutput={false}
          />
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
            <Icon name="Eye" size={20} className="text-accent" />
            <span>Live Preview</span>
          </h3>
          <div className="rounded-lg border border-border bg-muted/30 p-6 min-h-64">
            {isRunning ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center space-x-2 text-text-secondary">
                  <div className="animate-spin">
                    <Icon name="Loader2" size={20} />
                  </div>
                  <span>Rendering component...</span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-text-secondary">
                  Component Preview: {examples?.find(ex => ex?.id === selectedExample)?.name}
                </div>
                <div className="bg-white rounded-lg p-4 shadow-soft">
                  <div className="text-center text-text-secondary">
                    <Icon name="Play" size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Click "Run" to see the component in action</p>
                  </div>
                </div>
                {output && (
                  <div className="bg-background rounded-lg p-4 border border-border">
                    <pre className="text-sm text-text-secondary whitespace-pre-wrap">{output}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Tips */}
      <div className="bg-muted/30 rounded-lg p-4">
        <h4 className="font-medium mb-2 flex items-center space-x-2">
          <Icon name="Lightbulb" size={16} className="text-warning" />
          <span>Pro Tips</span>
        </h4>
        <ul className="text-sm text-text-secondary space-y-1">
          <li>• Modify the code above to experiment with different React patterns</li>
          <li>• Try adding new state variables or event handlers</li>
          <li>• Explore different styling approaches and component structures</li>
          <li>• Use the browser's developer tools to inspect the rendered output</li>
        </ul>
      </div>
    </div>
  );
};

export default ReactPlayground;